import React, { useEffect, useState } from "react";
import {
  FaClipboardList,
  FaTags,
  FaWarehouse,
  FaTruck,
  FaCheckCircle,
  FaDollarSign,
  FaChevronRight,
  FaChevronLeft,
} from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import GeneralItemInfo from "../components/GeneralItemInfo";
import SKUVariantManagement from "../components/SKUVariantManagement";
import InventoryManagement from "../components/InventoryManagement";
import ProcurementSupplyChain from "../components/ProcurementSupplyChain";
import SalesConfiguration from "../components/SalesConfiguration";
import QualityCompliance from "../components/QualityCompliance";
import {
  useCreateItem,
  useUpdateItem,
} from "../../_helper/actions/mutation/items";
import { ApiEndPoint } from "../../_helper/constant/api-types";
import { useAtom } from "jotai";
import { clinicAtom, pharmacyAtom } from "../../_helper/constant/constant-atom";
import { useItemsFilter } from "../../_helper/actions/app/items";

const uomMaster = [
  { value: "Unit", label: "Unit" },
  { value: "Bottle", label: "Bottle" },
  { value: "Box", label: "Box" },
  { value: "Kg", label: "Kilogram" },
  { value: "L", label: "Liter" },
  { value: "Tablet", label: "Tablet" },
];

const steps = [
  {
    id: "gen_info",
    label: "General Info",
    icon: <FaClipboardList />,
    component: (formData, setFormData) => (
      <GeneralItemInfo formData={formData} setFormData={setFormData} />
    ),
  },
  {
    id: "sku_management",
    label: "SKU Management",
    icon: <FaTags />,
    component: (formData, setFormData, handleSaveSkuData, setIsSkuSaved) => (
      <SKUVariantManagement
        formData={formData}
        setFormData={setFormData}
        onSave={handleSaveSkuData}
        setIsSkuSaved={setIsSkuSaved} // Pass the function here
      />
    ),
  },
  {
    id: "inventory",
    label: "Inventory",
    icon: <FaWarehouse />,
    component: (formData, setFormData) => (
      <InventoryManagement
        formData={formData}
        setFormData={setFormData}
        uomMaster={uomMaster}
      />
    ),
  },
  {
    id: "procurement",
    label: "Procurement",
    icon: <FaTruck />,
    component: (formData, setFormData) => (
      <ProcurementSupplyChain
        formData={formData}
        setFormData={setFormData}
        uomMaster={uomMaster}
      />
    ),
  },
  {
    id: "quality",
    label: "Quality & Compliance",
    icon: <FaCheckCircle />,
    component: (formData, setFormData) => (
      <QualityCompliance formData={formData} setFormData={setFormData} />
    ),
  },
  {
    id: "sales",
    label: "Sales",
    icon: <FaDollarSign />,
    component: (formData, setFormData) => (
      <SalesConfiguration
        formData={formData}
        setFormData={setFormData}
        uomMaster={uomMaster}
      />
    ),
  },
];

export default function ItemMaster() {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({}); // State to store form data
  const [isMenuCollapsed, setIsMenuCollapsed] = useState(false);
  const [isSkuSaved, setIsSkuSaved] = useState(false);
  const [clinic] = useAtom(clinicAtom);
  const [pharamcy] = useAtom(pharmacyAtom);

  const navigate = useNavigate();
  const { id } = useParams();

  const { data, isFetched } = useItemsFilter(
    ApiEndPoint.INVENTORY,
    `?action=getItem&id=${id}`,
    id && id !== "new"
  );

  const mutation = useCreateItem(ApiEndPoint.INVENTORY);
  const update_mutation = useUpdateItem(ApiEndPoint.INVENTORY);

  const isLastStep = currentStep === steps.length - 1;

  // Handle Finish Button Click
  const handleFinish = () => {
    console.log("Formdata", formData);
  };

  // Handle SKU Data Save from SKUVariantManagement
  const handleSaveSkuData = (skuDimension) => {
    setFormData((prevData) => ({
      ...prevData,
      SkuDimension: skuDimension,
    }));
    setIsSkuSaved(true); // Mark SKU as saved
    console.log("Updated Form Data with SKU:", skuDimension);
  };

  const handleSaveUpdate = () => {
    if (id === "new" && currentStep === 0) {
      const payload = {
        action: "postItem",
        name: formData.item_name,
        clinic_id: clinic.id,
        pharamcy_id: pharamcy.id,
        ...formData,
      };
      mutation.mutate(payload, {
        onSuccess: (data) => {
          setCurrentStep(currentStep + 1);
        },
      });
    } else {
      update_mutation.mutate(
        {
          data: {
            id: id,
            action: "patchItem",
            ...formData,
            name: formData.item_name,
          },
        },
        {
          onSuccess: () => {
            setCurrentStep(currentStep + 1);
            isLastStep && handleFinish();
          },
        }
      );
    }
  };

  useEffect(() => {
    if (isFetched) {
      setFormData(data);
    }
  }, [isFetched]);

  console.log(formData);

  return (
    <div className="min-h-screen flex bg-gray-50">
      {/* Sidebar */}
      <div
        className={`bg-white shadow-lg transition-all duration-300 ${
          isMenuCollapsed ? "w-16" : "w-64"
        }`}
      >
        <div className="flex items-center justify-between p-4 border-b">
          <h2
            className={`text-lg font-bold transition-opacity ${
              isMenuCollapsed ? "opacity-0 hidden" : "opacity-100"
            }`}
          >
            Item Master
          </h2>
          <button
            className="text-gray-500 hover:text-gray-700"
            onClick={() => setIsMenuCollapsed(!isMenuCollapsed)}
          >
            {isMenuCollapsed ? (
              <FaChevronRight size={18} />
            ) : (
              <FaChevronLeft size={18} />
            )}
          </button>
        </div>
        <ul className="space-y-2 mt-4">
          {steps.map((step, index) => (
            <li
              key={step.id}
              onClick={() => setCurrentStep(index)}
              className={`flex items-center p-3 rounded-lg cursor-pointer text-sm md:text-base transition ${
                currentStep === index
                  ? "bg-blue-600 text-white font-bold"
                  : "hover:bg-gray-100 text-gray-600"
              }`}
            >
              <span className="text-xl">{step.icon}</span>
              {!isMenuCollapsed && <span className="ml-3">{step.label}</span>}
            </li>
          ))}
        </ul>
      </div>

      {/* Main Content */}
      <div className="flex-grow p-6 md:p-12 bg-white shadow-md mx-auto max-w-5xl">
        {/* Back Button */}
        <button
          className="mb-4 flex items-center space-x-2 px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-md text-sm md:text-base"
          onClick={() => navigate(-1)}
        >
          <FaChevronLeft /> {/* Icon */}
          <span>Back</span>
        </button>

        {/* Main Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-700">
            {steps[currentStep].label}
          </h1>
          <p className="text-gray-500 mt-2">
            Step {currentStep + 1} of {steps.length}
          </p>
        </div>

        {/* Dynamic Component Rendering */}
        <div>
          {steps[currentStep].component(
            formData,
            setFormData,
            handleSaveSkuData,
            setIsSkuSaved
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between items-center mt-8">
          <button
            className={`px-6 py-2 rounded-md text-white text-sm md:text-base ${
              currentStep === 0
                ? "bg-gray-300 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700"
            }`}
            disabled={currentStep === 0}
            onClick={() => setCurrentStep(currentStep - 1)}
          >
            Previous
          </button>
          <button
            className={`px-6 py-2 rounded-md text-white text-sm md:text-base ${
              (currentStep === 1 && !isSkuSaved) || isLastStep
                ? "bg-gray-300 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700"
            }`}
            disabled={(currentStep === 1 && !isSkuSaved) || isLastStep}
            onClick={handleSaveUpdate}
          >
            {isLastStep ? "Finish" : "Next"}
          </button>
          {/* Display message if on SKU management step and SKU is not saved */}
          {currentStep === 1 && !isSkuSaved && (
            <p className="text-red-500 mt-2 text-sm">
              Please save the SKU variants before proceeding to the next step.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
